import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useResource } from '~/api';
import { getNotifications, readNotifications } from '~/components/layout/api.ts';
import Avatar from '~/components/new/Avatar';
import Button from '~/components/new/Button';
import List from '~/components/new/List';
import { RemixIconNames, IconNames } from '~/components/ui/Icon';
import Modal, { ModalAPI } from '~/components/ui/Modal';
import { Notification as NotificationType } from '~/types/platform.ts';
import { monthName, relativeTime } from '~/utils/format';
import { classNames } from '~/utils/styles';

export function NotificationsMenu() {
    const { data } = useResource(getNotifications, [], 'getNotifications');
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false);
    const notifications = data || [];
    const unread = !checked && notifications.find((n) => !n.read);

    return (
        <div className='topbar-notifications'>
            <Button
                variant='floating'
                icon={RemixIconNames.RiNotificationLine}
                title='Notification inbox'
                className={classNames({
                    'topbar-notifications-btn': true,
                    'topbar-notifications-btn-unread': Boolean(unread),
                })}
                onClick={() => {
                    setOpen(true);
                    setChecked(true);
                }}
                size='medium'
            />
            {open && (
                <Inbox
                    onClose={() => setOpen(false)}
                    notifications={notifications}
                />
            )}
        </div>
    );
}

function Inbox({
    onClose,
    notifications,
}: {
    onClose: () => void;
    notifications: NotificationType[];
}) {
    const [read, setRead] = useState(false);
    const unread = notifications.filter((n) => !n.read).length;
    useEffect(() => {
        if (unread > 0 && !read) {
            void readNotifications().then(() => {
                setRead(true);
            });
        }
    }, [unread, read]);

    const modalRef = useRef<ModalAPI>(null!);

    return (
        <Modal
            onClose={onClose}
            usePortal
            ref={modalRef}
            className='inbox'>
            <Modal.Header caption={unread > 0 ? `(${unread})` : ''}>Inbox</Modal.Header>
            <Modal.Body>
                <List>
                    <>
                        {notifications.length === 0 && (
                            <div className='p-8'>You have no notifications.</div>
                        )}
                        {notifications.length > 0 &&
                            notifications.map((n, i) => (
                                <Notification
                                    key={i}
                                    onClick={() => {
                                        void modalRef.current.close();
                                    }}
                                    {...n}
                                />
                            ))}
                    </>
                </List>
            </Modal.Body>
        </Modal>
    );
}

function Notification({
    data,
    time,
    type,
    read,
    onClick,
}: NotificationType & { onClick: () => void }) {
    const navigate = useNavigate();
    if (!notificationTypes[type]) return null;
    const link = notificationTypes[type].link?.(data);

    return (
        <List.Row
            dot={!read}
            icon={
                <Avatar
                    icon={notificationTypes[type].icon}
                    variant='light'
                />
            }
            onClick={
                link
                    ? () => {
                          onClick();
                          void navigate(link);
                      }
                    : undefined
            }
            title={notificationTypes[type].content(data)}
            ellipsis={false}
            caption={relativeTime(new Date(time))}
        />
    );
}

const notificationTypes: Record<
    string,
    {
        icon: IconNames;
        content: (data: Record<string, any>) => string;
        link?: (data: Record<string, any>) => string | undefined;
    }
> = {
    // For talent
    invoice_approved: {
        icon: RemixIconNames.RiBillFill,
        link: ({ id }) => `/invoices/details/${id}`,
        content: ({ month }) => `You approved your invoice for ${monthName(Number(month))}`,
    },
    invoice_paid: {
        icon: RemixIconNames.RiBillFill,
        link: ({ id }) => `/invoices/details/${id}`,
        content: ({ month }) => `Your invoice for ${monthName(Number(month))} has been paid ✨`,
    },
    expense_submitted: {
        icon: RemixIconNames.RiCurrencyLine,
        link: ({ id }) => `/expenses/details/${id}`,
        content: ({ kind }) => `You submitted a ${kind} expense`,
    },
    expense_approved: {
        icon: RemixIconNames.RiCurrencyLine,
        link: ({ id }) => `/expenses/details/${id}`,
        content: ({ kind }) => `Your ${kind} expense has been approved 🎉`,
    },
    job_opening_applied: {
        icon: RemixIconNames.RiSendPlaneFill,
        link: ({ permalink }) => (permalink ? `/position/${permalink}` : undefined),
        content: ({ title, company }) => `You applied to the position ${title} at ${company}.`,
    },
    job_opening_disqualified: {
        icon: RemixIconNames.RiSendPlaneFill,
        link: ({ permalink }) => `/position/${permalink}`,
        content: ({ title, company }) => `You have been disqualified from ${title} at ${company}.`,
    },
    job_opening_declined: {
        icon: RemixIconNames.RiSendPlaneFill,
        link: ({ permalink }) => `/position/${permalink}`,
        content: ({ title, company }) => `You declined ${title} at ${company}.`,
    },
    job_opening_stage_moved: {
        icon: RemixIconNames.RiSendPlaneFill,
        link: ({ permalink }) => `/position/${permalink}`,
        content: ({ title, company, stage }) =>
            `Your next step with ${company} for the role of ${title} will be ${stage}.`,
    },

    // For customers
    customer_profile_published: {
        icon: RemixIconNames.RiBuilding2Fill,
        link: ({ permalink }) => `/company/${permalink}`,
        content: () => `Your company profile has been published!`,
    },
    job_opening_published: {
        icon: RemixIconNames.RiSuitcaseFill,
        link: ({ permalink }) => `/job_positions/${permalink}`,
        content: ({ title }) => `Your new position, ${title}, has been published!`,
    },
    job_opening_candidate_moved_forward: {
        icon: RemixIconNames.RiSendPlaneFill,
        link: ({ id, application_id }) =>
            `/job_positions/${id}/active/pipeline/candidate/${application_id}`,
        content: ({ title, name, stage }) =>
            ` ${name} has been moved forward to ${stage} for the role of ${title}`,
    },
    offer_letter_sent: {
        icon: RemixIconNames.RiArticleLine,
        content: ({ name, position }) =>
            position
                ? `Offer letter for ${position} has been sent to ${name}`
                : `Offer letter has been sent to ${name}`,
    },
    offer_letter_accepted_customer: {
        icon: RemixIconNames.RiArticleLine,
        content: ({ name, position }) =>
            position
                ? `Offer letter for ${name} for the position ${position} has been accepted`
                : `Offer letter for ${name} has been accepted`,
    },
    offer_letter_rejected_customer: {
        icon: RemixIconNames.RiArticleLine,
        content: ({ name, position }) =>
            position
                ? `Offer letter for ${name} for the position ${position} has been rejected`
                : `Offer letter for ${name} has been rejected`,
    },
    offer_letter_signed_customer: {
        icon: RemixIconNames.RiArticleLine,
        content: ({ name, position }) =>
            position
                ? `${name} signed the offer letter for the position ${position} 🎉`
                : `${name} signed the offer letter 🎉`,
    },
    signed_contract_customer: {
        icon: RemixIconNames.RiSuitcaseFill,
        content: ({ name, position }) => `${name} signed a contract agreement for ${position}`,
    },
    raise_created_manager: {
        icon: RemixIconNames.RiVipCrown2Fill,
        content: ({ contractor }) => `The raise you submitted for ${contractor} has been approved!`,
    },
    bonus_approved_manager: {
        icon: RemixIconNames.RiVipCrown2Fill,
        content: ({ contractor }) =>
            `The bonus you submitted for ${contractor} will be included in the next pay cycle`,
    },
    pto_approved_manager: {
        icon: RemixIconNames.RiCalendarFill,
        content: ({ from, to, name }) =>
            `You approved ${name}'s time off request for ${from} - ${to}`,
    },
    pto_rejected_manager: {
        icon: RemixIconNames.RiCalendarFill,
        content: ({ from, to, name }) =>
            `You rejected ${name}'s time off request for ${from} - ${to}`,
    },
    expense_approved_manager: {
        icon: RemixIconNames.RiCurrencyLine,
        link: ({ id }) => `/customer/expenses/details/${id}`,
        content: ({ kind, name }) => `You approved ${name}'s ${kind} expense reimbursement`,
    },
    expense_rejected_manager: {
        icon: RemixIconNames.RiCurrencyLine,
        link: ({ id }) => `/customer/expenses/details/${id}`,
        content: ({ kind, name }) => `You rejected ${name}'s ${kind} expense reimbursement`,
    },
    equipment_status_change: {
        icon: RemixIconNames.RiMacbookFill,
        link: () => `/customer/equipment`,
        content: ({ status, contractor, brand, model }) => {
            switch (status) {
                case 'ordered':
                case 'purchased':
                    return `${contractor}'s equipment, ${brand} ${model}, has been ordered!`;
                case 'shipped':
                    return `${contractor}'s equipment has shipped!`;
                case 'delivered':
                    return `${contractor}'s equipment has been delivered!`;
                default:
                    return `${contractor}'s equipment, ${brand} ${model}, has been ${status}`;
            }
        },
    },
    bank_account_approved: {
        icon: RemixIconNames.RiBankFill,
        link: () => `/profile/bank-accounts`,
        content: ({ account_number }) =>
            `Your bank account ${account_number.slice(-6)} was approved. It's now available for payments.`,
    },
    bank_account_rejected: {
        icon: RemixIconNames.RiBankFill,
        link: () => `/profile/bank-accounts`,
        content: ({ account_number }) =>
            `Your bank account ${account_number.slice(-6)} wasn't approved. The holder name doesn't match your contracting entity legal name. Add a new account where the names match.`,
    },
    legal_name_change_approved: {
        icon: RemixIconNames.RiUser2Fill,
        link: () => `/profile/personal_info`,
        content: () => `Your legal name change was approved. Your account has been updated.`,
    },
    country_change_approved: {
        icon: RemixIconNames.RiFlagFill,
        link: () => `/profile/personal_info`,
        content: () => `Your country change was approved. Your account has been updated.`,
    },
    country_change_rejected: {
        icon: RemixIconNames.RiFlagFill,
        link: () => `/profile/personal_info`,
        content: () =>
            `Your country change wasn't approved. Our Dev Success team will contact you to discuss next steps.`,
    },
};
